import { useState, useRef, useEffect } from 'react';
import Multitrack from 'wavesurfer-multitrack';
import type { Stem } from '@/types/stem';

interface Track {
  id: number;
  name: string;
  url: string;
  isMuted: boolean;
  isSoloed: boolean;
  volume: number;
  color: string;
}

interface MultitrackInstance {
  play: () => void;
  pause: () => void;
  destroy: () => void;
  getDuration: () => number;
  setTrackVolume: (trackId: number, volume: number) => void;
  on: (event: string, callback: (arg: any) => void) => void;
}

interface TrackConfig {
  id: number;
  url: string;
  volume: number;
  startPosition: number;
  options: {
    waveColor: string;
    progressColor: string;
    height: number;
    barWidth: number;
    barGap: number;
    barRadius: number;
  };
}

interface TrackLoadingState {
  isLoading: boolean;
  progress: number;
  error: string | null;
}

const fetchWithProgress = async (
  url: string, 
  trackId: number, 
  onProgress: (trackId: number, progress: number) => void,
  abortController: AbortController
): Promise<ArrayBuffer> => {
  const response = await fetch(url, {
    signal: abortController.signal,
    credentials: 'omit'  // Don't send credentials for Backblaze URLs
  });
  const reader = response.body?.getReader();
  const contentLength = Number(response.headers.get('Content-Length')) || 0;
  
  if (!reader) {
    throw new Error('Failed to get reader from response');
  }

  let receivedLength = 0;
  const chunks: Uint8Array[] = [];

  while (true) {
    const { done, value } = await reader.read();
    
    if (done) {
      break;
    }

    chunks.push(value);
    receivedLength += value.length;
    const progress = (receivedLength / contentLength) * 100;
    onProgress(trackId, progress);
  }

  const chunksAll = new Uint8Array(receivedLength);
  let position = 0;
  for (const chunk of chunks) {
    chunksAll.set(chunk, position);
    position += chunk.length;
  }

  return chunksAll.buffer;
};

export const useMultitrack = (stems: Stem[], originalUrl: string) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [masterVolume, setMasterVolume] = useState(1);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loadProgress, setLoadProgress] = useState<Record<number, number>>({});
  
  const multitrackRef = useRef<MultitrackInstance | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stemColors = [
    '#50A090', // Green
    '#66C6BF', // Teal/Green-Blue
    '#5D9AC8', // Blue
    '#4A74A0', // Dark Blue
    '#68669A', // Purple
    '#A074AA', // Magenta
    '#C28EBA', // Pink
  ];

  const trackLoadProgress = (trackId: number, progress: number) => {
    setLoadProgress(prev => {
      if (prev[trackId] && prev[trackId] >= progress) {
        return prev;
      }
      return {
        ...prev,
        [trackId]: progress
      };
    });
  };

  const shouldMuteByDefault = (name: string) => {
    const lowercaseName = name.toLowerCase();
    return lowercaseName === 'original' || lowercaseName === 'all drums' || lowercaseName === 'all vocals';
  };

  useEffect(() => {
    if (stems.length > 0) {
      const initTracks = stems.map((stem, index) => ({
        id: index,
        name: stem.name,
        url: stem.url,
        isMuted: shouldMuteByDefault(stem.name),
        isSoloed: false,
        volume: 1,
        color: stemColors[index % stemColors.length],
      }));

      setTracks(initTracks);

      const setupMultitrack = async () => {
        if (!containerRef.current) return undefined;

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const initialProgress = initTracks.reduce((acc, track) => ({
          ...acc,
          [track.id]: 0
        }), {});
        setLoadProgress(initialProgress);
        
        try {
          const audioBuffers = await Promise.all(
            initTracks.map(track => 
              fetchWithProgress(
                track.url, 
                track.id, 
                trackLoadProgress,
                abortControllerRef.current!
              ).catch(error => {
                console.error(`Error loading track ${track.id}:`, error);
                return null;
              })
            )
          );

          if (multitrackRef.current) {
            multitrackRef.current.destroy();
          }

          const blobUrls: string[] = [];
          const trackConfigs = initTracks.map((track, index) => {
            const audioBuffer = audioBuffers[index];
            if (!audioBuffer) {
              throw new Error(`Failed to load audio for track ${track.id}`);
            }

            const blob = new Blob([audioBuffer], { type: 'audio/mp3' });
            const blobUrl = URL.createObjectURL(blob);
            blobUrls.push(blobUrl);

            return {
              id: track.id,
              url: blobUrl,
              volume: track.volume,
              startPosition: 0,
              options: {
                waveColor: track.color,
                progressColor: '#00000099',
                height: 50,
                barWidth: 0,
                barGap: 0,
                barRadius: 0,
              },
            };
          }) as TrackConfig[];

          const instance = Multitrack.create(
            trackConfigs,
            {
              container: containerRef.current,
              cursorColor: '#FFFFFF',
              cursorWidth: 1,
            }
          ) as unknown as MultitrackInstance;

          instance.on('canplay', () => {
            setIsLoading(false);
            const duration = stems[0].duration;
            setDuration(duration);

            // Call updateTrackVolumes to handle default muting and volume setup
            updateTrackVolumes();

            const finalProgress = initTracks.reduce((acc, track) => ({
              ...acc,
              [track.id]: 100
            }), {});
            setLoadProgress(finalProgress);
          });

          multitrackRef.current = instance;

          return () => {
            blobUrls.forEach(url => {
              URL.revokeObjectURL(url);
            });
          };

        } catch (error) {
          console.error('Error creating multitrack:', error);
          setIsLoading(false);
          return undefined;
        }
      };

      let cleanup: (() => void) | undefined;
      setupMultitrack().then(cleanupFn => {
        cleanup = cleanupFn;
      });

      return () => {
        if (cleanup) cleanup();
        if (multitrackRef.current) {
          multitrackRef.current.destroy();
        }
      };
    }
  }, [stems, originalUrl]);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    const updateZoom = () => {
      if (!multitrackRef.current) return;

      const containerWidth = container.clientWidth;
      const avgDuration = stems[0]?.duration || 180;
      const pxPerSec = containerWidth / avgDuration;

      // Call the zoom method on the Multitrack instance
      (multitrackRef.current as any).zoom(pxPerSec);
    };

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      updateZoom();
    });

    resizeObserver.observe(container);

    // Initial zoom calculation
    updateZoom();

    return () => {
      resizeObserver.disconnect();
    };
  }, [stems]);

  const togglePlayPause = () => {
    if (multitrackRef.current) {
      if (isPlaying) {
        multitrackRef.current.pause();
      } else {
        multitrackRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const updateTrackVolumes = () => {
    if (!multitrackRef.current) return;

    setTracks(prevTracks => {
      const hasSoloedTracks = prevTracks.some(track => track.isSoloed);

      prevTracks.forEach(track => {
        const effectiveVolume = hasSoloedTracks
          ? track.isSoloed
            ? track.volume * masterVolume
            : 0
          : track.isMuted
          ? 0
          : track.volume * masterVolume;

        multitrackRef.current!.setTrackVolume(track.id, effectiveVolume);
      });

      return prevTracks; // Return the same state to avoid unnecessary re-renders
    });
  };

  const setTrackVolume = (trackId: number, volume: number) => {
    setTracks(prevTracks =>
      prevTracks.map(track =>
        track.id === trackId ? { ...track, volume } : track
      )
    );
    updateTrackVolumes();
  };

  const setTrackSolo = (trackId: number) => {
    setTracks(prevTracks =>
      prevTracks.map(track =>
        track.id === trackId
          ? { ...track, isSoloed: !track.isSoloed }
          : track
      )
    );
    updateTrackVolumes();
  };

  const setTrackMute = (trackId: number) => {
    setTracks(prevTracks =>
      prevTracks.map(track =>
        track.id === trackId
          ? { ...track, isMuted: !track.isMuted }
          : track
      )
    );
    updateTrackVolumes();
  };

  const setGlobalVolume = (volume: number) => {
    setMasterVolume(volume);
    updateTrackVolumes();
  };

  const resetPlayback = () => {
    if (multitrackRef.current) {
      console.log('Reset playback not implemented in current library version');
    }
  };

  return {
    containerRef,
    isPlaying,
    isLoading,
    currentTime,
    duration,
    tracks,
    masterVolume,
    loadProgress,
    togglePlayPause,
    setTrackVolume,
    setTrackMute,
    setTrackSolo,
    setGlobalVolume,
  };
};
